import { z } from "zod";

import { timestampSchema } from "~/modules/ai-edit-tool/schemas";
import { jerboaBrowserInfoSchema } from "~/modules/jerboa/schemas";

export const jerboaClientPreviewStatusSchema = z.enum(["success", "error", "interrupted"]);
export type JerboaClientPreviewStatus = z.infer<typeof jerboaClientPreviewStatusSchema>;

const jerboaClientPreviewTriggerSchema = z.enum(["pause", "unmount", "init_error", "fatal_error"]);

export const jerboaClientPreviewSourcePlatformSchema = z.enum(["web", "ios"]);
export type JerboaClientPreviewSourcePlatform = z.infer<
  typeof jerboaClientPreviewSourcePlatformSchema
>;

const jerboaClientPreviewEditTypeSchema = z.enum(["captions", "ai-edit"]);

const jerboaClientPreviewAspectRatioSchema = z.enum(["9:16", "16:9"]);

const jerboaClientPreviewEventSchema = z.object({
  source: z.string(),
  name: z.string(),
  timestamp: z.string(),
  properties: z.record(z.string(), z.unknown()),
});

const jerboaClientPreviewSummarySchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  videoId: z.string().nullish(),
  projectId: z.string(),
  captionsProjectId: z.string().nullish(),
  sourcePlatform: jerboaClientPreviewSourcePlatformSchema.default("web"),
  appVersion: z.string().nullish(),
  jerboaVersion: z.string().nullish(),
  jerboaVersionShort: z.string().nullish(),
  editType: jerboaClientPreviewEditTypeSchema.nullish(),
  aspectRatio: jerboaClientPreviewAspectRatioSchema,
  status: jerboaClientPreviewStatusSchema,
  trigger: jerboaClientPreviewTriggerSchema,
  sessionDurationMs: z.number(),
  initDurationMs: z.number(),
  framesRendered: z.number(),
  totalErrors: z.number(),
  width: z.number(),
  height: z.number(),
  avgPlaybackFps: z.number().nullish(),
  avgFrameRenderMs: z.number().nullish(),
  p90PlaybackFps: z.number().nullish(),
  p90FrameRenderMs: z.number().nullish(),
  targetFps: z.number().nullish(),
  peakWasmMemoryMb: z.number().nullish(),
  peakJsHeapMb: z.number().nullish(),
  peakWasmLiveMb: z.number().nullish(),
  peakNativePhysicalFootprintMb: z.number().nullish(),
  createdAt: timestampSchema,
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
});

const jerboaClientPreviewDetailSchema = jerboaClientPreviewSummarySchema.extend({
  projectProtoUri: z.string().nullish(),
  errorsByCategory: z.record(z.string(), z.number()).nullish(),
  sampleErrorMessages: z.array(z.string()).nullish(),
  wasmLoadDurationMs: z.number().nullish(),
  wasmBytesLoaded: z.number().nullish(),
  playingDurationMs: z.number().nullish(),
  maxFrameRenderMs: z.number().nullish(),
  slowFrameCount: z.number().nullish(),
  p50PlaybackFps: z.number().nullish(),
  p99PlaybackFps: z.number().nullish(),
  p50FrameRenderMs: z.number().nullish(),
  p99FrameRenderMs: z.number().nullish(),
  longestFrameGapMs: z.number().nullish(),
  p50FrameGapMs: z.number().nullish(),
  p90FrameGapMs: z.number().nullish(),
  p99FrameGapMs: z.number().nullish(),
  hitchFrameShare: z.number().nullish(),
  avgPrepareCpuMs: z.number().nullish(),
  gpuBusyMs: z.number().nullish(),
  decoderErrorCount: z.number().nullish(),
  stalledOnDecodeCount: z.number().nullish(),
  forcedRenderCount: z.number().nullish(),
  starvedTotalMs: z.number().nullish(),
  starvedLongestMs: z.number().nullish(),
  suspendedMs: z.number().nullish(),
  suspendCount: z.number().nullish(),
  inferredSuspendCount: z.number().nullish(),
  fpsBuckets: z.array(z.number()).nullish(),
  bucketWindowMs: z.number().nullish(),
  avgWasmMemoryMb: z.number().nullish(),
  avgJsHeapMb: z.number().nullish(),
  avgWasmLiveMb: z.number().nullish(),
  wasmMemoryBuckets: z.array(z.number()).nullish(),
  jsHeapBuckets: z.array(z.number()).nullish(),
  wasmLiveBuckets: z.array(z.number()).nullish(),
  avgNativePhysicalFootprintMb: z.number().nullish(),
  nativePhysicalFootprintBuckets: z.array(z.number()).nullish(),
  playbackStartProjectMs: z.number().nullish(),
  playbackEndProjectMs: z.number().nullish(),
  assetUrlMap: z.record(z.string(), z.string()).nullish(),
  browserInfo: jerboaBrowserInfoSchema.nullish(),
  events: z.array(jerboaClientPreviewEventSchema),
});

export const jerboaClientPreviewsListResponseSchema = z.object({
  page: z.array(jerboaClientPreviewSummarySchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
});

export const jerboaClientPreviewDetailResponseSchema = z.object({
  result: jerboaClientPreviewDetailSchema,
});

const playbackSegmentAggregateSchema = z.object({
  totalCount: z.number(),
  scoredCount: z.number(),
  errorCount: z.number(),
  errorRate: z.number(),
  avgFpsMean: z.number().default(0),
  avgFpsMedian: z.number().default(0),
  // Nullish, not `.default(0)`: the Datadog RUM aggregate API has no pc10, so
  // buckets aggregated from RUM since 2026-08-31 omit p10 entirely. A
  // defaulted 0 would plot as a measured floor of zero fps.
  avgFpsP10: z.number().nullish(),
  avgFpsP90: z.number().default(0),
  avgFpsSub30Share: z.number().nullish(),
  // Share of playing time the synchronous render call held the main thread
  // (median per session), the share of sessions where that exceeded 60%, and
  // the population behind both (differs from scoredCount).
  renderBusyCount: z.number().nullish(),
  renderBusyShareMedian: z.number().nullish(),
  renderBoundSessionShare: z.number().nullish(),
  // Pipeline utilization (binding stage: max of CPU incl. prepare, GPU),
  // the GPU stage alone, the cannot-keep-up classifier (material starvation
  // or near-saturation — thresholds live in the shadowing-analysis
  // aggregator, the single home), and hitch pacing.
  utilizationCount: z.number().nullish(),
  utilizationMedian: z.number().nullish(),
  gpuBusyShareMedian: z.number().nullish(),
  cannotKeepUpShare: z.number().nullish(),
  hitchFrameShareMedian: z.number().nullish(),
});

export type PlaybackSegmentAggregate = z.infer<typeof playbackSegmentAggregateSchema>;

const playbackRollingAggregateSchema = playbackSegmentAggregateSchema.extend({
  p90FpsMedian: z.number().default(0),
});

const playbackAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number(),
  scoredCount: z.number(),
  successCount: z.number(),
  errorCount: z.number(),
  interruptedCount: z.number(),
  errorRate: z.number(),
  avgFpsMean: z.number().default(0),
  avgFpsMedian: z.number().default(0),
  // See the segment schema above: no pc10/pc25 in the Datadog RUM aggregate
  // API, so these are absent — not zero — on RUM-sourced buckets.
  avgFpsP10: z.number().nullish(),
  avgFpsP25: z.number().nullish(),
  avgFpsP75: z.number().default(0),
  avgFpsP90: z.number().default(0),
  p90FpsMedian: z.number().default(0),
  avgFrameRenderMsMedian: z.number(),
  p90FrameRenderMsMedian: z.number(),
  slowFrameRatioMedian: z.number(),
  portraitCount: z.number(),
  portraitAvgFpsMedian: z.number(),
  landscapeCount: z.number(),
  landscapeAvgFpsMedian: z.number(),
  appVersions: z.array(z.string()).nullish(),
  appVersionCounts: z.record(z.string(), z.number()).nullish(),
  appPrimaryVersion: z.string().nullish(),
  appPrimaryVersionShare: z.number().nullish(),
  appVersionCoverage: z.number().nullish(),
  appVersionFirstSeenInBucket: z.record(z.string(), z.number()).nullish(),
  releaseMarkers: z.array(z.object({ version: z.string(), firstSeenAt: z.number() })).nullish(),
  appVersionFirstSeenMap: z.record(z.string(), z.number()).nullish(),
  jerboaVersions: z.array(z.string()).nullish(),
  jerboaVersionCounts: z.record(z.string(), z.number()).nullish(),
  jerboaPrimaryVersion: z.string().nullish(),
  jerboaPrimaryVersionShare: z.number().nullish(),
  jerboaVersionCoverage: z.number().nullish(),
  jerboaVersionFirstSeenInBucket: z.record(z.string(), z.number()).nullish(),
  jerboaReleaseMarkers: z
    .array(z.object({ version: z.string(), firstSeenAt: z.number() }))
    .nullish(),
  jerboaVersionFirstSeenMap: z.record(z.string(), z.number()).nullish(),
  // Nullish, not `.default(0)`: buckets predating these metrics omit them and
  // are never rewritten, so a defaulted 0 would plot as history that was never
  // measured. Same inside segments. The older `.default(0)` fields above are
  // the opposite convention and stay safe only because each reader guards on
  // `scoredCount > 0` — see JRB-1820 for unifying them.
  avgFpsSub30Share: z.number().nullish(),
  longestFrameGapMsMedian: z.number().nullish(),
  longestFrameGapMsP90: z.number().nullish(),
  starvedSessionShare: z.number().nullish(),
  starvedTotalMsP90WhenStarved: z.number().nullish(),
  starvedLongestMsP90WhenStarved: z.number().nullish(),
  // Severity tiers: share of sessions starved for more than 1/5/10% of their
  // playing time. `starvedSessionShare` (> 0ms) trips on a single play-start
  // warm-up blip and reads ~55%; these separate the sessions a user noticed.
  starvedOver1PctShare: z.number().nullish(),
  starvedOver5PctShare: z.number().nullish(),
  starvedOver10PctShare: z.number().nullish(),
  renderBusyCount: z.number().nullish(),
  renderBusyShareMedian: z.number().nullish(),
  renderBusyShareP90: z.number().nullish(),
  renderBoundSessionShare: z.number().nullish(),
  utilizationCount: z.number().nullish(),
  utilizationMedian: z.number().nullish(),
  gpuBusyShareMedian: z.number().nullish(),
  cannotKeepUpShare: z.number().nullish(),
  hitchFrameShareMedian: z.number().nullish(),
  p90FrameGapMsMedian: z.number().nullish(),
  osSegments: z.record(z.string(), playbackSegmentAggregateSchema).nullish(),
  browserSegments: z.record(z.string(), playbackSegmentAggregateSchema).nullish(),
  gpuSegments: z.record(z.string(), playbackSegmentAggregateSchema).nullish(),
  // Keyed by raw source_platform ("web" / "ios") — the segment that lets a
  // reader ask about one platform directly (JRB-1826).
  platformSegments: z.record(z.string(), playbackSegmentAggregateSchema).nullish(),
  rolling1d: playbackRollingAggregateSchema.nullish(),
  rolling7d: playbackRollingAggregateSchema.nullish(),
});

export type PlaybackAnalysisBucket = z.infer<typeof playbackAnalysisBucketSchema>;

export const playbackAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(playbackAnalysisBucketSchema),
  }),
});
